/**
 * ONE derivation of the company-profile option labels, for every app that renders them.
 *
 * D-22 (Duplikat-Audit 29.07.): the main portal once carried a SECOND label family for these
 * enums (`onboarding.assistant.industry.*` with PascalCase keys and different texts) — the user
 * picked "Produktion / Industrie" in the chat and read "Fertigung" in the plan snapshot. The fix
 * was to make the key derivation a single shared function; putting a per-app copy back (now that
 * the Phoenix portal renders the same dropdowns) would reintroduce exactly that bug class, which
 * is why this lives in the library and the i18n prefixes are constants rather than string
 * literals at the call sites.
 *
 * CAUTION, second spelling in circulation: `snapshot-details` and `company-settings-changes`
 * derive the same text from the enum VALUE (`value.toLowerCase()`, i.e. `soleproprietorship`
 * instead of `sole_proprietorship`). BOTH spellings live under `company_settings.*` and must be
 * maintained together on text changes — they are NOT dead keys.
 */

/** Canonical i18n prefixes for the profile enums (bundle: `company_settings.json`). */
export const COMPANY_PROFILE_I18N = {
  businessTypes: 'company_settings.business_types',
  industries: 'company_settings.industries',
} as const;

/** A selectable option: the wire value plus the i18n key its label is translated from. */
export interface CompanyProfileLabelOption {
  value: string;
  labelKey: string;
}

/**
 * PascalCase enum NAME → i18n key under `i18nPrefix` (snake_case; special case LLC → `llc`).
 * The single derivation of these keys — see the D-22 note above.
 */
export function enumLabelKey(enumName: string, i18nPrefix: string): string {
  if (enumName === 'LLC') {
    return `${i18nPrefix}.llc`;
  }
  const snakeCase = enumName
    .replace(/([A-Z])/g, '_$1')
    .toLowerCase()
    .replace(/^_/, '');
  return `${i18nPrefix}.${snakeCase}`;
}

/**
 * Every member of a string enum as `{ value, labelKey }`, in declaration order. Callers
 * translate `labelKey` themselves, so this stays free of ngx-translate (and of Angular).
 */
export function enumLabelOptions(
  enumObject: Record<string, string>,
  i18nPrefix: string
): CompanyProfileLabelOption[] {
  return Object.keys(enumObject).map((name) => ({
    value: enumObject[name],
    labelKey: enumLabelKey(name, i18nPrefix),
  }));
}

/**
 * Employee-count brackets are NUMERIC RANGES, not prose — they are rendered verbatim in every
 * language (mirroring the backend's Display attribute) and deliberately have no i18n keys.
 * Unknown names fall through to the enum name so a newly added bracket is visible rather than
 * blank; the library spec asserts full coverage so that fallback never ships.
 */
export function employeeCountDisplayName(enumName: string): string {
  switch (enumName) {
    case 'Range1To10':
      return '1-10';
    case 'Range11To50':
      return '11-50';
    case 'Range51To200':
      return '51-200';
    case 'Range201To500':
      return '201-500';
    case 'Range501To1000':
      return '501-1000';
    case 'Range1001To5000':
      return '1001-5000';
    case 'RangeOver5000':
      return '5000+';
    default:
      return enumName;
  }
}
